import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface PagePermissionResult {
  canAccess: boolean;
  isLoading: boolean;
  isSuperAdmin: boolean;
  hasPermission: (feature: string, action: string) => boolean;
}

const LEVEL_RANK: Record<string, number> = {
  off: 0,
  view: 1,
  contribute: 2,
  manage: 3,
};

/**
 * Hook to check if the current user has permission to access a page.
 * Uses Sentinel's Permissions type (flat feature map with level + scope)
 * and AppMetadata (is_super_admin from JWT).
 */
export function usePagePermission(
  feature: string | null = null,
  action: string | null = null,
): PagePermissionResult {
  const { user, appMetadata, loading, session } = useAuth();
  const location = useLocation();

  const result = useMemo(() => {
    // Only show loading during initial auth bootstrap (App.tsx blocks until this resolves).
    // Do NOT return isLoading:true for session-exists-but-user-pending — that causes
    // a redundant spinner cascade on every page after the app has already loaded.
    if (loading) {
      return { canAccess: true, isLoading: true, isSuperAdmin: false };
    }

    // Session exists but user profile not yet loaded — allow access silently
    // rather than flashing a spinner. The profile will arrive momentarily.
    if (session && !user) {
      return { canAccess: true, isLoading: false, isSuperAdmin: false };
    }

    if (!user) {
      return { canAccess: false, isLoading: false, isSuperAdmin: false };
    }

    const isSuperAdmin = appMetadata?.is_super_admin === true;
    if (isSuperAdmin) {
      return { canAccess: true, isLoading: false, isSuperAdmin: true };
    }

    // Check specific feature/action
    if (feature) {
      const featurePerms = user.permissions?.[feature];
      if (!featurePerms) return { canAccess: false, isLoading: false, isSuperAdmin: false };

      if (action) {
        // Map action to required level — must match AuthContext.hasPermission
        const actionLevel: Record<string, string> = {
          view: 'view', list: 'view',
          create: 'contribute', edit: 'contribute', update: 'contribute', contribute: 'contribute', approve: 'manage',
          delete: 'manage', cancel: 'manage', manage: 'manage', process: 'manage',
          send: 'manage', whatsapp: 'manage', unlink_draft: 'manage',
          test_send: 'contribute',
        };
        const requiredLevel = actionLevel[action] || 'manage';
        const userLevel = featurePerms.level || 'off';
        const canAccess = (LEVEL_RANK[userLevel] || 0) >= (LEVEL_RANK[requiredLevel] || 0);
        return { canAccess, isLoading: false, isSuperAdmin: false };
      }

      // Any access to feature
      const canAccess = !!featurePerms.level && featurePerms.level !== 'off';
      return { canAccess, isLoading: false, isSuperAdmin: false };
    }

    // Default: deny for non-admin users without explicit feature permission
    return { canAccess: false, isLoading: false, isSuperAdmin: false };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, appMetadata, loading, location.pathname, feature, action]);

  const checkPermission = useMemo(
    (): ((feature: string, action: string) => boolean) => {
      return (feat: string, act: string): boolean => {
        if (loading || !user) return false;
        if (appMetadata?.is_super_admin === true) return true;
        const featurePerms = user.permissions?.[feat];
        if (!featurePerms) return false;
        const actionLevel: Record<string, string> = {
          view: 'view', list: 'view',
          create: 'contribute', edit: 'contribute', approve: 'contribute',
          delete: 'manage', cancel: 'manage',
          send: 'manage', whatsapp: 'manage', unlink_draft: 'manage',
        };
        const requiredLevel = actionLevel[act] || 'manage';
        return (LEVEL_RANK[featurePerms.level] || 0) >= (LEVEL_RANK[requiredLevel] || 0);
      };
    },
    [user, appMetadata, loading],
  );

  return { ...result, hasPermission: checkPermission };
}

export function usePermission(feature: string, action: string): boolean {
  const { user, appMetadata, loading } = useAuth();

  return useMemo(() => {
    if (loading || !user) return false;
    if (appMetadata?.is_super_admin === true) return true;
    const featurePerms = user.permissions?.[feature];
    if (!featurePerms) return false;
    const actionLevel: Record<string, string> = {
      view: 'view', list: 'view',
      create: 'contribute', edit: 'contribute', approve: 'contribute',
      delete: 'manage', cancel: 'manage',
      send: 'manage', whatsapp: 'manage', unlink_draft: 'manage',
    };
    const requiredLevel = actionLevel[action] || 'manage';
    return (LEVEL_RANK[featurePerms.level] || 0) >= (LEVEL_RANK[requiredLevel] || 0);
  }, [user, appMetadata, loading, feature, action]);
}

export default usePagePermission;
